/**
 * criteriaTypes — study-side AI eligibility review criteria types
 * (docs/crm-eligibility-review.md §2.2 `crm.study_review_criteria_sets` /
 * §2.3 `crm.study_review_criteria`).
 *
 * Kept OUT of crm/types.ts on purpose: the lead-side eligibility types land
 * there concurrently, and this file is the study-page (criteria lifecycle)
 * half of the split.
 */

/**
 * Criteria-set lifecycle: `extracting` (worker running) → `draft` (editable)
 * → `confirmed` (immutable; confirming retires any prior confirmed set) |
 * `failed`. Exactly one confirmed and at most one open (extracting/draft)
 * set per study.
 */
export type ReviewCriteriaSetStatus = 'extracting' | 'draft' | 'confirmed' | 'retired' | 'failed';

export type ReviewCriterionKind = 'inclusion' | 'exclusion';

export type ReviewCriterionCategory =
  | 'demographics'
  | 'medical_history'
  | 'medications'
  | 'laboratory'
  | 'procedures'
  | 'lifestyle'
  | 'reproductive'
  | 'consent'
  | 'other';

export const REVIEW_CRITERION_CATEGORIES: { value: ReviewCriterionCategory; label: string }[] = [
  { value: 'demographics', label: 'Demographics' },
  { value: 'medical_history', label: 'Medical history' },
  { value: 'medications', label: 'Medications' },
  { value: 'laboratory', label: 'Laboratory' },
  { value: 'procedures', label: 'Procedures' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'reproductive', label: 'Reproductive' },
  { value: 'consent', label: 'Consent' },
  { value: 'other', label: 'Other' },
];

/** Display label for a category, tolerating unknown values. */
export function reviewCategoryLabel(value: string | null | undefined): string {
  const found = REVIEW_CRITERION_CATEGORIES.find((c) => c.value === value);
  if (found) return found.label;
  return (value || 'Other').replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
}

/**
 * A leaf or group node of a compiled criterion `rule` (facts engine, the
 * prescreen rule vocabulary — see `evalCriterionRule` in backend
 * `crmPivot.js`, mirrored 1:1). Leaf ops: eq/neq/gt/gte/lt/lte/between/in/
 * exists. `fact` names which `rule_facts` entry the leaf reads (omittable
 * when `rule_facts` has exactly one entry). Group nodes combine child
 * verdicts with `combinator`. This type is intentionally loose (`any`-ish
 * shape) — the structured builder only round-trips the vocabulary it
 * supports; anything else stays editable only via the raw-JSON fallback.
 */
export interface CriterionRuleLeaf {
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'in' | 'exists';
  fact?: string;
  value?: string | number | boolean;
  min?: number;
  max?: number;
  values?: (string | number)[];
}

export interface CriterionRuleGroup {
  combinator: 'and' | 'or';
  conditions: CriterionRule[];
}

export type CriterionRule = CriterionRuleLeaf | CriterionRuleGroup;

/** `fact_type` vocabulary for `rule_facts` entries (facts engine). */
export type CriterionFactType =
  | 'lab_result'
  | 'vital'
  | 'condition'
  | 'medication'
  | 'procedure'
  | 'family_history'
  | 'social_history'
  | 'demographic'
  | 'test_event'
  | 'other';

/** One `rule_facts` entry — which fact key/type a rule leaf reads, and its
 *  recency window (null = fact-type default per §13.4: labs 365d, vitals
 *  180d, condition/medication/demographic unwindowed). */
export interface CriterionRuleFact {
  fact_type: CriterionFactType;
  key: string;
  window_days: number | null;
  unit: string | null;
  select?: 'status' | 'value_num';
}

/** One row of `crm.study_review_criteria` (§2.3). */
export interface StudyReviewCriterion {
  id: string;
  set_id: string;
  criterion_kind: ReviewCriterionKind;
  /** 'I-1', 'E-3' … unique per set (app layer). */
  criterion_number: string;
  category: ReviewCriterionCategory;
  /** VERBATIM protocol wording. */
  criterion_text: string;
  /** Layman restatement (display aid). */
  plain_language: string | null;
  search_terms: string[];
  is_critical: boolean;
  /** Protocol page provenance. */
  source_page: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // ---- Facts engine (Phase 2) — compiled predicate, editable draft-only ----
  /** The criterion-MET predicate over facts; null = not machine-checkable
   *  (stays an LLM judgment call at Tier 1+). */
  rule?: CriterionRule | null;
  /** Which fact keys the rule reads (see CriterionRuleFact). */
  rule_facts?: CriterionRuleFact[] | null;
  /** Sex-scoping: 'any' (default) | 'female' | 'male'. Resolved in code from
   *  a demographic sex fact; the other sex ⇒ not_applicable. */
  applies_to?: 'any' | 'female' | 'male';
  /** Verbatim qualifier phrases from the criterion text (e.g. 'active',
   *  'unstable') that gate whether Tier 0 may resolve a condition-status leaf. */
  qualifiers?: string[];
  /** 'auto' (default; Tier 0 attempted when rule/applies_to present) |
   *  'deterministic' (always attempt Tier 0) | 'llm' (skip Tier 0 entirely). */
  tier_hint?: 'auto' | 'deterministic' | 'llm';
}

/** One row of `crm.study_review_criteria_sets` (§2.2). */
export interface StudyReviewCriteriaSet {
  id: string;
  network_id: string;
  study_id: string;
  version: number;
  status: ReviewCriteriaSetStatus;
  source_document_ids: string[];
  extraction_model_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  error: string | null;
  notes: string | null;
  created_by: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  /** Joined rows on GET /api/crm/studies/:id/review-criteria. */
  criteria?: StudyReviewCriterion[];
  /** Optional display name join for confirmed_by (rendered when present). */
  confirmed_by_name?: string | null;
}

/** GET /api/crm/studies/:id/review-criteria — confirmed + open + recent retired. */
export interface ReviewCriteriaSetsResponse {
  sets: StudyReviewCriteriaSet[];
}
